import Teams from '@gameday/common/components/form/Pages/Teams'
import AlertMessage from '@gameday/react-components/components/AlertMessage'
import Countdown from '@gameday/react-components/components/Countdown'
import { get, isEmpty } from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import config from '../../../../constants/config'
import { files } from '../../../../constants/endpoints'
import { privateRoutes } from '../../../../constants/routes/routes'

const StyledTeams = styled.div`
  --custom-primary-color: ${props => props.formSettings.textLinkColour};

  .MuiToggleButton-root.Mui-selected {
    border-color: ${props => props.formSettings.textLinkColour} !important;
    background-color: ${props => props.formSettings.textLinkColour} !important;
    &:hover {
      border-color: ${props => props.formSettings.textLinkColour} !important;
      background-color: ${props => props.formSettings.textLinkColour} !important;
    }
  }

  .MuiButton-containedPrimary {
    color: ${props => props.formSettings.buttonTextColour};
    background-color: ${props => props.formSettings.buttonColour};
    &:hover {
      background-color: ${props => props.formSettings.buttonColour};
    }
  }

  .country-select__control\.country-select__control--is-focused {
    border-color: ${props => props.formSettings.textLinkColour} !important;
  }
`

const StyledAlertMessage = styled(AlertMessage)`
  margin-bottom: 20px;
`

const StyledCountdown = styled(Countdown)`
  margin: 10px 0px;
`

const TeamsComponent = ({
  questions,
  userId,
  form,
  defaultCountry,
  formType,
  email,
  formSettings,
  teamsByCode = [],
  teamsByUser = [],
  getTeamsByUser,
  getTeamsByCode,
  isTeamEvent,
  getAllTeamCodes,
  teamCodes,
  setTeam,
  setTeamFormType,
  registeredTeam,
  getTeamsInEvent,
  teamsInEvent = [],
  urlParams = {},
  eventTeamLimit = {},
  teamWelcomeMessages,
  ...rest
}) => {
  const { teamLimitExceeded = false } = eventTeamLimit || {}
  const { teamActivationDate, activationDate } = formSettings

  const eventTeamIds = teamsInEvent.map(t => t.teamId) || []
  const teamsByCodeInEvent = teamsByCode.filter(t => eventTeamIds.includes(t.id)) || []

  const [type, setType] = useState('Join A Team')
  const [isNextDisabled, setNextDisabled] = useState(false)
  const [defaults, setDefaults] = useState({
    defaultMapping: {},
    defaultValues: {},
    revertMapping: {},
  })
  const questionMap = {
    registerOrJoinATeam: 'Register or Join A Team',
    registerATeam: 'Register A Team',
    newOrExistingTeam: 'New or Existing Team',
    selectTeam: 'Select Existing Team',
    teamCode: 'Team Code',
    inpuTeamCode: 'Input Team Code',
    logo: 'Team Logo',
    teamName: 'Team Name',
    teamEmail: 'Team Email',
    teamPrimaryContact: 'Team Primary Contact',
    teamMobile: 'Mobile Number',
  }

  const { buttonColour, buttonTextColour } = formSettings || {}

  const { teamType, teamCode } = urlParams
  let teamFormType = teamType === 'register' ? 'Register A Team' : 'Join A Team'

  if (teamLimitExceeded) {
    teamFormType = 'Join A Team'
  }

  const renderDefaultMapping = () => {
    if (isEmpty(registeredTeam.defaultQuestions)) {
      // eslint-disable-next-line no-param-reassign
      registeredTeam = {
        ...registeredTeam,
        defaultQuestions: {
          ...registeredTeam.defaultQuestions,
          teamEmail: email,
          ...(teamType && {
            registerOrJoinATeam: teamFormType,
          }),
          ...(teamType === 'join' && teamCode && { inputTeamCode: teamCode }),
        },
      }
    }

    const defaultMapping = {}
    const defaultValues = registeredTeam.defaultQuestions || {}
    const revertMapping = {}

    Object.keys(questionMap).forEach(key => {
      const sfid = get(
        questions.find(q => get(q, 'relatedQuestion.name') === questionMap[key]),
        'relatedQuestion.sfid'
      )

      const defaultValue = get(
        questions.find(q => get(q, 'relatedQuestion.name') === questionMap[key]),
        'relatedQuestion.defaultValue'
      )

      if (!sfid) {
        return
      }

      defaultValues[sfid] = registeredTeam.defaultQuestions[sfid] || defaultValue
      defaultMapping[sfid] = key
      revertMapping[key] = sfid
    })

    setDefaults({
      defaultMapping,
      defaultValues,
      revertMapping,
    })
  }

  useEffect(() => {
    getAllTeamCodes()
    getTeamsByUser()
    getTeamsInEvent()
  }, [])

  useEffect(() => {
    setTeamFormType(type)
  }, [type])

  useEffect(() => {
    renderDefaultMapping()
  }, [teamLimitExceeded])

  const IsDateInRange = currentTab => {
    // eslint-disable-next-line no-shadow
    const { activationDate, expiryDate, teamActivationDate, teamExpiryDate, currentTime } = formSettings
    const { utc_datetime = moment() } = currentTime

    let aDate = activationDate
    let eDate = expiryDate

    if (currentTab === 'Register A Team') {
      aDate = teamActivationDate
      eDate = teamExpiryDate
    }

    if (moment(utc_datetime).isBefore(aDate)) {
      return { date: aDate, status: 'Scheduled' }
    }

    if (moment(utc_datetime).isAfter(eDate)) {
      return { date: eDate, status: 'Closed' }
    }

    return { date: '', status: 'Open' }
  }

  const CountdownAlert = dateStatus => {
    const { date, status } = dateStatus
    let AlertComponent = <></>

    switch (status) {
      case 'Closed':
        AlertComponent = (
          <>
            <p>Registration is closed.</p>
            <p> {moment(date).format('dddd Do MMMM, YYYY')} </p>
          </>
        )
        break
      case 'Scheduled':
        AlertComponent = (
          <>
            <p>Registration is currently closed. They will open in</p>
            <StyledCountdown
              date={type === 'Register A Team' ? moment(teamActivationDate) : moment(activationDate)}
              completeMessage="Registration is now open!"
              dateFormat="DD MM YYYY, h:mm a"
              size="md"
            />
            <p> {moment(date).format('dddd Do MMMM, YYYY')} </p>
          </>
        )
        break
      default:
        break
    }
    return AlertComponent
  }

  return (
    <StyledTeams formSettings={formSettings}>
      <StyledAlertMessage variant="error" title="Registration Closed" show={isNextDisabled}>
        {CountdownAlert(IsDateInRange(type))}
      </StyledAlertMessage>
      {teamLimitExceeded && (
        <StyledAlertMessage variant="info" title="Team Limit Reached" show>
          The maximum number of teams have been registered to this event. You may still join a team below.
        </StyledAlertMessage>
      )}
      <Teams
        questions={questions}
        formData={{
          userId,
          form,
          // biome-ignore lint/complexity/useOptionalChain: TODO
          pageName: (privateRoutes(isTeamEvent).find(r => r.id === 'teams') || {}).title || 'Teams',
          profileId: userId,
          application: config.application,
          uploadEndpoint: `api/${files.uploadDirect}`,
          documentUploadEndpoint: `api/${files.documentUploadDirect}`,
          downloadEndpoint: `api/${files.downloadDirect}`,
        }}
        onSubmit={values => {
          const st = teamsByUser[0].value.find(t => t.id === values.selectTeam)
          const mappedValues = Object.keys(defaults.revertMapping).reduce((acc, r) => {
            acc[r] = values[defaults.revertMapping[r]]
            return acc
          }, {})

          setTeam({
            isTeamReset: false,
            defaultQuestions: values,
            ...(type === 'Join A Team' && {
              teamDetails: {
                ...values.inputTeamCode,
              },
            }),
            ...(st && type === 'Register A Team'
              ? {
                  teamDetails: {
                    id: st.id,
                    teamCode: st.teamCode,
                    name: st.name,
                    teamEmail: st.teamEmail,
                    teamLogo: st.teamLogo,
                    teamMobileNumber: st.teamMobileNumber,
                    teamPrimaryContact: st.teamPrimaryContact,
                  },
                }
              : type === 'Register A Team' && {
                  teamDetails: {
                    ...mappedValues,
                    name: mappedValues.teamName,
                  },
                }),
          })
        }}
        onChange={values => {
          setNextDisabled(IsDateInRange(values.registerOrJoinATeam).status !== 'Open')
          setType(values.registerOrJoinATeam)
        }}
        defaultValue={defaults.defaultValues}
        defaultCountry={defaultCountry}
        teamsByCode={teamsByCode}
        teamsByCodeInEvent={teamsByCodeInEvent}
        teamsByUser={teamsByUser}
        getTeamsByCode={getTeamsByCode}
        teamCodes={teamCodes}
        teamsInEvent={teamsInEvent}
        teamWelcomeMessages={teamWelcomeMessages}
        disableQuestions={teamLimitExceeded}
        buttonColour={buttonColour}
        buttonTextColour={buttonTextColour}
        isNextDisabled={isNextDisabled}
        {...rest}
      />
    </StyledTeams>
  )
}

export default TeamsComponent
